<script lang="ts" setup>
import type { IconConstant } from '@tpg/grammar-web-icons';
import { tpgIconGpsFixed, tpgIconGpsNotFixed, tpgIconNavigation } from '@tpg/grammar-web-icons';
import { computed } from 'vue';

import { GrammarPrimaryButton } from '@nora-monorepo/grammar-ui';
import { useGenericUserStore } from '@nora-monorepo/ui-shared';

import { MapUtils } from '#nora-ui/map/map/MapUtils';
import { useMapStore } from '#nora-ui/map/stores/map-store';

interface MapControlsProps {
  mapId: string;
}

const props = defineProps<MapControlsProps>();
const ZOOM_LEVEL_FOLLOW = 15;
const ZOOM_LEVEL_HEADING_UP = 18;

const mapStore = useMapStore();
const userStore = useGenericUserStore();

const map = mapStore.getMapByIdOrThrow(props.mapId);

const lastUserTrack = computed(() => {
  if (!userStore.user) {
    return;
  }

  const lastTrack = userStore.user.lastTrack;

  if (!lastTrack) {
    return;
  }

  return lastTrack;
});

const isFollowingUser = computed(() => {
  if (!userStore.user) {
    return false;
  }

  return map.ref.followedProducerId === userStore.user.id;
});

function handleGotoUserLocation() {
  if (!lastUserTrack.value) {
    return;
  }

  const { latitude, longitude } = lastUserTrack.value;

  map.setCenter({ latitude, longitude, zoom: ZOOM_LEVEL_FOLLOW });

  if (userStore.user) {
    map.ref.setFollowedProducerId(userStore.user.id);
  }
}

function handleRotateToUserHeading() {
  if (!lastUserTrack.value || lastUserTrack.value.heading === null || lastUserTrack.value.heading === undefined) {
    return;
  }

  const mapRotationInRadians = MapUtils.userHeadingDegreesToMapRotationRadians(lastUserTrack.value.heading);
  map.setRotation(mapRotationInRadians);
}

interface LocationControl {
  iconTemplate: IconConstant | '';
  onClick: () => void;
}

const locationControl = computed<LocationControl>(() => {
  if (!isFollowingUser.value) {
    return {
      iconTemplate: tpgIconGpsNotFixed,
      onClick: handleGotoUserLocation,
    };
  }

  if (!map.ref.isInHeadingUpMode) {
    return {
      iconTemplate: tpgIconGpsFixed,
      onClick: () => {
        map.setZoom(ZOOM_LEVEL_HEADING_UP);
        map.ref.setHeadingUpMode(true);
        handleRotateToUserHeading();
      },
    };
  }

  return {
    iconTemplate: tpgIconNavigation,
    onClick: () => {
      map.ref.setHeadingUpMode(false);
    },
  };
});
</script>

<template>
  <GrammarPrimaryButton
    :disabled="!lastUserTrack"
    :icon-template="locationControl.iconTemplate"
    @click="locationControl.onClick"
  />
</template>
